import { useState } from 'react';
import styled from 'styled-components';
import { Collapse, Divider, Space } from 'antd';
import { DownOutlined, FileTextOutlined, WarningFilled } from '@ant-design/icons';
import Typography from 'antd/lib/typography/Typography';
import SuitCaseIcon from 'components/icons/SuitCaseIcon';
import DeclarationStatusTag from 'components/ui/composed/declarations/declaration-status/DeclarationStatusTag';
import { DeclarationStatuses } from 'store/declarations/enums/common/declaration-status';
import { ManifestDeclarationsResponse } from 'store/manifests/types';

const formatNotificationMessage = (numDeclarations: number) => {
    return numDeclarations === 1
        ? '1 declaration will not be amended because it is not in an amendable status.'
        : `${numDeclarations} declarations will not be amended because they are not in an amendable status.`;
};

const StyledCollapse = styled(Collapse)`
    margin-top: 8px;

    .ant-collapse-content-box {
        padding: 0 !important;
    }
    .ant-collapse-header {
        padding: 0 !important;
    }
`;

const { Panel } = Collapse;

type RowProps = {
    jobReferenceId: string;
    mrn: string | null;
    status: DeclarationStatuses;
    hasDivider: boolean;
};

const DeclarationRow = ({ jobReferenceId, mrn, status, hasDivider }: RowProps) => (
    <li style={{ display: 'contents' }}>
        <Space
            size={4}
            style={{
                padding: '3px 0',
                alignItems: 'center',
                borderTop: hasDivider ? '1px solid var(--gray-3)' : undefined,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <SuitCaseIcon style={{ fontSize: 16, color: 'var(--brand-6)' }} />
            </div>
            <span>{jobReferenceId}</span>
        </Space>
        <Space
            size={4}
            style={{
                padding: '3px 0',
                alignItems: 'center',
                borderTop: hasDivider ? '1px solid var(--gray-3)' : undefined,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <FileTextOutlined style={{ fontSize: 16, color: 'var(--brand-6)' }} />
            </div>
            <span>{mrn ?? 'MRN not available'}</span>
        </Space>
        <div
            style={{
                padding: '3px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                borderTop: hasDivider ? '1px solid var(--gray-3)' : undefined,
            }}
        >
            <DeclarationStatusTag status={status as any} small />
        </div>
    </li>
);

type Props = {
    data: ManifestDeclarationsResponse[];
};

const NonAmendableDeclarationsSection = ({ data }: Props) => {
    const [activeKey, setActiveKey] = useState<string[]>([]);

    const isOpen = activeKey.includes('KEY');

    const totalDeclarations = data.reduce((sum, job) => sum + job.declarations.length, 0);

    const rows = data.flatMap((job) =>
        job.declarations.map((dec) => ({
            key: dec.declarationId,
            jobReferenceId: job.jobReferenceId,
            mrn: dec.mrn,
            status: dec.status,
        }))
    );

    return (
        <div
            style={{
                backgroundColor: 'var(--orange-1)',
                border: '1px solid var(--orange-3)',
                padding: '12px 16px',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
            }}
        >
            <WarningFilled style={{ color: 'var(--orange-6)', fontSize: 16, marginTop: 2 }} />

            <div style={{ flex: 1 }}>
                <Typography>{formatNotificationMessage(totalDeclarations)}</Typography>

                <StyledCollapse
                    bordered={false}
                    ghost
                    expandIcon={() => null}
                    style={{ padding: 0 }}
                    activeKey={activeKey}
                    onChange={(key) => setActiveKey(key as string[])}
                >
                    <Panel
                        key="KEY"
                        header={
                            <div style={{ color: 'var(--brand-6)', userSelect: 'none' }}>
                                <DownOutlined
                                    style={{
                                        transform: isOpen ? 'rotate(180deg)' : undefined,
                                        transition: 'transform 0.36s',
                                    }}
                                />{' '}
                                <span>{isOpen ? 'Hide Details' : 'See Details'}</span>
                            </div>
                        }
                    >
                        <Divider style={{ marginBlock: 12 }} />

                        <ul
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 2fr 3fr',
                                listStyle: 'none',
                                padding: 0,
                                margin: 0,
                                columnGap: 6,
                                maxHeight: 400,
                                overflowY: 'auto',
                            }}
                        >
                            {rows.map((row, index) => (
                                <DeclarationRow
                                    key={row.key}
                                    jobReferenceId={row.jobReferenceId}
                                    mrn={row.mrn}
                                    status={row.status}
                                    hasDivider={index > 0}
                                />
                            ))}
                        </ul>
                    </Panel>
                </StyledCollapse>
            </div>
        </div>
    );
};

export default NonAmendableDeclarationsSection;
